import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { HttpService } from '@app/core/services/http.service';
import { ProfileState } from '@app/layout/common/profile/state/profile.state';
import { VesselReportPagination } from '@chartering/vessel-reports/state/vessel-reports.types';
import { ReportTypesEnum } from '@chartering/vessel-reports/vessel-reports.constants';
import { environment } from '@environments/environment';
import { LIMIT_PER_PAGE_DEFAULT } from '@shared/shared.constants';
import { MovementDetail, VesselReport, VesselSchedule } from '@vessel-reports/state/vessel-reports.types';
import { VesselApi } from '@vessel/vessel.constants';
import { BehaviorSubject, map, Observable, of, switchMap, tap, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class VesselReportsService {
  public static readonly API: string = `${environment.PROJECT_API}/vessel-report-group/reports`;
  public static readonly VESSEL_MOVEMENT_API: string = `${environment.PROJECT_API}/vessel_movements`;
  public static readonly VESSEL_API: string = VesselApi.vessels;

  protected readonly httpService: HttpService = inject(HttpService);
  protected readonly profileState: ProfileState = inject(ProfileState);
  private _selectedReportType: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  private _vesselReportsLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  get vesselReportsLoading$(): Observable<boolean> {
    return this._vesselReportsLoading.asObservable();
  }

  getVesselReports(type: string, queryParams?: Params): Observable<any> {
    this._vesselReportsLoading.next(true);
    this._selectedReportType.next(type);

    const defaultParams = {
      page: '1',
      limit: LIMIT_PER_PAGE_DEFAULT,
      'filter[report_type_id]': type
    };

    return this.httpService
      .get<any>(`${VesselReportsService.API}`, {
        params: new HttpParams({ fromObject: Object.keys(queryParams)?.length ? queryParams : defaultParams })
      })
      .pipe(
        map((response) => ({
          pagination: {
            current_page: response.meta.current_page,
            last_page: response.meta.last_page,
            per_page: response.meta.per_page,
            total: response.meta.total,
            movements: response.meta?.movements
          },
          data: response.data
        })),
        tap(() => {
          this._vesselReportsLoading.next(false);
        }),
        switchMap((response) => {
          if (response.data === null) {
            return throwError(() => new Error('Requested page is not available!'));
          }

          return of(response);
        })
      );
  }

  getExcelPortReports(queryParams: Params): Observable<VesselReport[]> {
    return this.httpService
      .get<VesselReportPagination>(
        `${VesselReportsService.API}/excels?filter[report_type_id]=${ReportTypesEnum.ARRIVAL_REPORT},${ReportTypesEnum.DEPARTURE_REPORT}`,
        {
          params: {
            ...queryParams
          }
        }
      )
      .pipe(map((reports) => reports.data));
  }

  getExcelMonthlyRobs(queryParams: Params): Observable<VesselReport[]> {
    return this.httpService
      .get<VesselReportPagination>(`${VesselReportsService.API}/excels?filter[report_type_id]=${ReportTypesEnum.MONTHLY_ROB_REPORT}`, {
        params: {
          ...queryParams
        }
      })
      .pipe(map((reports) => reports.data));
  }

  getExcelNoonReports(queryParams: Params): Observable<VesselReport[]> {
    return this.httpService
      .get<VesselReportPagination>(
        `${VesselReportsService.API}/excels?filter[report_type_id]=${ReportTypesEnum.NOON_REPORT_SEA},${ReportTypesEnum.NOON_REPORT_PORT}`,
        {
          params: {
            ...queryParams
          }
        }
      )
      .pipe(map((reports) => reports.data));
  }

  getVesselMovements(vesselId: string, queryParams?: Params): Observable<any> {
    this._vesselReportsLoading.next(true);

    const defaultParams = {
      page: '1',
      limit: LIMIT_PER_PAGE_DEFAULT
    };

    return this.httpService
      .get<any>(`${VesselReportsService.VESSEL_MOVEMENT_API}`, {
        params: new HttpParams({ fromObject: Object.keys(queryParams)?.length ? queryParams : defaultParams })
      })
      .pipe(
        map((response) => ({
          pagination: {
            current_page: response.current_page,
            last_page: response.last_page,
            per_page: response.per_page,
            total: response.total
          },
          data: response.data,
          portcall: {
            total_bunkering_portcall: response.total_bunkering_portcall,
            total_discharging_portcall: response.total_discharging_portcall,
            total_loading_portcall: response.total_loading_portcall,
            total_other_portcall: response.total_other_portcall,
            total_portcall: response.total_portcall
          }
        })),
        tap(() => {
          this._vesselReportsLoading.next(false);
        }),
        switchMap((response) => {
          if (response.data === null) {
            return throwError(() => new Error('Requested page is not available!'));
          }

          return of(response);
        })
      );
  }

  deleteVesselReport(id: string) {
    return this.httpService.delete(`${VesselReportsService.API}/${id}`, {});
  }

  getVesselSchedule(startDate: number, endDate: number): Observable<VesselSchedule> {
    return this.httpService
      .get<{
        data: VesselSchedule[];
      }>(`${VesselReportsService.VESSEL_API}/relations/vessel_gantt`, {
        params: {
          'filter[id]': this.profileState.profileId(),
          range: `${startDate}|${endDate}`
        }
      })
      .pipe(
        switchMap((request) => {
          if (!request.data) {
            return throwError(() => new Error('Could not find schedule of current vessel!'));
          }

          return of(request.data);
        }),
        map((schedule) => {
          return schedule[0];
        })
      );
  }

  getMovementDetail(movement_id: string): Observable<MovementDetail> {
    return this.httpService
      .get<{
        data: MovementDetail;
      }>(`${VesselReportsService.VESSEL_MOVEMENT_API}/${movement_id}`)
      .pipe(
        switchMap((request) => {
          if (!request.data) {
            return throwError(() => new Error(`Could not find movement detail of ${movement_id}!`));
          }

          return of(request.data);
        })
      );
  }
}
